import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getDateTimeSetting } from 'store/selector/general.selector';
import {
  DateTimeInput,
  formatUserDate,
  formatUserDateTime,
  formatUserTime,
  resolveFormats,
  setCachedDateTimeSetting,
  ResolvedDateTimeFormats,
} from 'helpers/dateTimeFormat';

export interface UseDateTimeFormatResult extends ResolvedDateTimeFormats {
  setting: any;
  formatDate: (input: DateTimeInput) => string;
  formatTime: (input: DateTimeInput) => string;
  formatDateTime: (input: DateTimeInput) => string;
}

/** Reads Redux date/time prefs and keeps the module cache in sync for non-React helpers. */
export const useDateTimeFormat = (): UseDateTimeFormatResult => {
  const setting = useSelector(getDateTimeSetting);

  useEffect(() => {
    setCachedDateTimeSetting(setting);
  }, [setting]);

  const resolved = useMemo(() => resolveFormats(setting), [setting]);

  const formatDate = useCallback(
    (input: DateTimeInput) => formatUserDate(input, setting),
    [setting]
  );
  const formatTime = useCallback(
    (input: DateTimeInput) => formatUserTime(input, setting),
    [setting]
  );
  const formatDateTime = useCallback(
    (input: DateTimeInput) => formatUserDateTime(input, setting),
    [setting]
  );

  return {
    setting,
    ...resolved,
    formatDate,
    formatTime,
    formatDateTime,
  };
};

export default useDateTimeFormat;
